import { c as _c } from "react/compiler-runtime";
import Grid from '@precis-digital/kurama/Grid';
import { RechartsResponsiveContainer as ResponsiveContainer } from '@precis-digital/kurama/recharts';
import Skeleton from '@precis-digital/kurama/Skeleton';
import { useTheme } from '@precis-digital/kurama/utils';
import { type ReactElement } from 'react';
import { useChartCardConstants } from '../hooks/useChartCardConstants';
import { useChartCardDefaults } from '../hooks/useChartCardDefaults';
import { CHART_CARD_CARD_LAYOUT, CHART_CARD_PERFORMANCE_COLUMN_MIN_WIDTH } from '../constants';
import useChartChoicesElements from '../hooks/useChartChoicesElements';
import usePrepareData from '../hooks/usePrepareData';
import type { ChartCardPerformanceContainerProps, ElementDescriptionMetric, SecondAxisDefinition } from '../internalTypes';
import type { ChartCardCustomisationsProps, ChartDataRow, ViewType } from '../types';
import { getFirstOrThirdAxisDetails, getSpreadSecondAxisDetails } from '../utils/axis';
import { getChartCardChartContainerSize } from '../utils/card';
import { aggregateMetric, aggregateMetricRespectingRecalculate, isMetricSeriesAllZero, zeroFillMetricValues } from '../utils/data';
import { formatMetricValue } from '../utils/generics';
import { assignSeriesColorsFromDefinitions } from '../utils/options';
import ChartNaked from './ChartNaked';
import IconTemplates from './IconTemplates';
import StyledCard from './styled/StyledCard';
import StyledCardsContainer from './styled/StyledCardsContainer';
import StyledTypographyNoWrap from './styled/StyledTypographyNoWrap';
import TypographyTruncated from './TypographyTruncated';
const ChartCardPerformanceContainer = props => {
  const $ = _c(89);
  const {
    id,
    title,
    dimensionsMap,
    chart,
    respectColors: t0,
    hideChart: t1,
    hideTotalValue: t2,
    chartDrawing,
    drawingColorMaps,
    elementsDescriptionsMap,
    state,
    dispatch,
    t
  } = props;
  const respectColors = t0 === undefined ? false : t0;
  const hideChart = t1 === undefined ? false : t1;
  const hideTotalValue = t2 === undefined ? false : t2;
  const {
    choicesList,
    choicesMap,
    selections,
    firstAxisDomain,
    secondAxisDomains
  } = state;
  const {
    processedData,
    newMetricsMap,
    transposedMetricsLists
  } = usePrepareData(props);
  let t3;
  if ($[0] !== choicesList || $[1] !== choicesMap || $[2] !== dispatch || $[3] !== selections || $[4] !== t || $[5] !== transposedMetricsLists) {
    t3 = {
      useChips: false,
      choicesList,
      choicesMap,
      choicesTogether: false,
      transposedMetricsLists,
      selections,
      dispatch,
      t
    };
    $[0] = choicesList;
    $[1] = choicesMap;
    $[2] = dispatch;
    $[3] = selections;
    $[4] = t;
    $[5] = transposedMetricsLists;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    singleElements,
    multipleElements
  } = useChartChoicesElements(t3);
  const {
    type: chartType,
    firstAxis,
    secondAxis
  } = chart;
  let t4;
  if ($[7] !== choicesMap || $[8] !== dimensionsMap || $[9] !== elementsDescriptionsMap || $[10] !== firstAxis || $[11] !== newMetricsMap || $[12] !== selections) {
    t4 = getFirstOrThirdAxisDetails(firstAxis, {
      choicesMap,
      dimensionsMap,
      metricsMap: newMetricsMap,
      elementsDescriptionsMap,
      selections
    });
    $[7] = choicesMap;
    $[8] = dimensionsMap;
    $[9] = elementsDescriptionsMap;
    $[10] = firstAxis;
    $[11] = newMetricsMap;
    $[12] = selections;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const firstAxisDetails = t4;
  const defaults = useChartCardDefaults();
  const theme = useTheme();
  const {
    chartSeriesColors
  } = useChartCardConstants();
  let T0;
  let T1;
  let t10;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[14] !== chart || $[15] !== chartDrawing || $[16] !== chartSeriesColors || $[17] !== chartType || $[18] !== choicesMap || $[19] !== defaults || $[20] !== dimensionsMap || $[21] !== drawingColorMaps || $[22] !== elementsDescriptionsMap || $[23] !== firstAxisDetails || $[24] !== firstAxisDomain || $[25] !== hideChart || $[26] !== hideTotalValue || $[27] !== id || $[28] !== multipleElements || $[29] !== newMetricsMap || $[30] !== processedData || $[31] !== respectColors || $[32] !== secondAxis || $[33] !== secondAxisDomains || $[34] !== selections || $[35] !== singleElements || $[36] !== theme || $[37] !== title || $[38] !== transposedMetricsLists) {
    const secondAxisDefinition = {
      chartType,
      axisType: "second",
      axis: secondAxis,
      color: theme.tokens.foreground.main.link,
      customProps: undefined,
      axisDrawing: undefined
    };
    let t11;
    if ($[47] !== elementsDescriptionsMap) {
      t11 = detail => elementsDescriptionsMap[detail.dataKeys[0]]?.color;
      $[47] = elementsDescriptionsMap;
      $[48] = t11;
    } else {
      t11 = $[48];
    }
    const secondAxisDetails = assignSeriesColorsFromDefinitions({
      items: getSpreadSecondAxisDetails({
        definition: secondAxisDefinition,
        choicesMap,
        dimensionsMap,
        metricsMap: newMetricsMap,
        transposedMetricsLists,
        selections
      }),
      getDefinedColor: t11,
      chartSeriesColors
    });
    T1 = StyledCard;
    if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <IconTemplates />;
      $[49] = t9;
    } else {
      t9 = $[49];
    }
    let t12;
    if ($[50] !== theme) {
      t12 = theme.spacing(2);
      $[50] = theme;
      $[51] = t12;
    } else {
      t12 = $[51];
    }
    let t13;
    if ($[52] !== title) {
      t13 = <Grid minWidth={0} flex={1} overflow="hidden"><TypographyTruncated variant="h3">{title}</TypographyTruncated></Grid>;
      $[52] = title;
      $[53] = t13;
    } else {
      t13 = $[53];
    }
    let t14;
    if ($[54] !== theme) {
      t14 = theme.spacing(2);
      $[54] = theme;
      $[55] = t14;
    } else {
      t14 = $[55];
    }
    let t15;
    if ($[56] !== multipleElements || $[57] !== singleElements || $[58] !== t14) {
      t15 = <Grid display="flex" alignItems="center" gap={t14} width="fit-content" flexShrink={0}>{singleElements}{multipleElements}</Grid>;
      $[56] = multipleElements;
      $[57] = singleElements;
      $[58] = t14;
      $[59] = t15;
    } else {
      t15 = $[59];
    }
    if ($[60] !== t12 || $[61] !== t13 || $[62] !== t15) {
      t10 = <Grid display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" gap={t12} width="100%">{t13}{t15}</Grid>;
      $[60] = t12;
      $[61] = t13;
      $[62] = t15;
      $[63] = t10;
    } else {
      t10 = $[63];
    }
    T0 = StyledCardsContainer;
    t5 = `chart-cards-container-${id}`;
    t6 = CHART_CARD_PERFORMANCE_COLUMN_MIN_WIDTH;
    t7 = CHART_CARD_CARD_LAYOUT.cardsFixedColumns;
    let t16;
    if ($[64] !== chart || $[65] !== chartDrawing || $[66] !== defaults || $[67] !== drawingColorMaps || $[68] !== elementsDescriptionsMap || $[69] !== firstAxisDetails || $[70] !== firstAxisDomain || $[71] !== hideChart || $[72] !== hideTotalValue || $[73] !== newMetricsMap || $[74] !== processedData || $[75] !== respectColors || $[76] !== secondAxisDomains) {
      t16 = secondAxisDetail => {
        const dataKey = secondAxisDetail.dataKeys[0];
        const metricDescription = elementsDescriptionsMap[dataKey] as ElementDescriptionMetric;
        const metricLabel = metricDescription?.label ?? dataKey;
        const metric = newMetricsMap[dataKey];
        const value = (metric == null ? aggregateMetric(processedData, {
          id: dataKey,
          ...metricDescription
        }) : aggregateMetricRespectingRecalculate(processedData, metric, newMetricsMap)) ?? 0;
        const isAllZero = isMetricSeriesAllZero(processedData, dataKey);
        const renderAsLine = chart.type === "gradientLine" && isAllZero;
        const overrideChart = renderAsLine ? {
          ...chart,
          type: "line" as const
        } : chart;
        const overrideSecondAxisDetail = renderAsLine ? {
          ...secondAxisDetail,
          chartType: "line" as const
        } : secondAxisDetail;
        const chartData = isAllZero ? zeroFillMetricValues(processedData, dataKey) : processedData;
        const secondAxisDomain = isAllZero ? [0, 1] as const : secondAxisDomains[0];
        const formattedValue = formatMetricValue(dataKey, metricDescription, value, defaults.axis.format);
        return <StyledCard key={dataKey} innerCard={true}><TypographyTruncated variant="h5">{metricLabel}</TypographyTruncated>{!hideChart && <ResponsiveContainer minWidth={defaults.cardChartSizes.performance.minWidth} height={defaults.cardChartSizes.performance.height}><ChartNaked data={chartData} chart={overrideChart} firstAxisDetails={firstAxisDetails} secondAxisDetail={overrideSecondAxisDetail} respectColors={respectColors ?? false} chartDrawing={chartDrawing} drawingColorMaps={drawingColorMaps} elementsDescriptionsMap={elementsDescriptionsMap} firstAxisDomain={firstAxisDomain} secondAxisDomain={secondAxisDomain} /></ResponsiveContainer>}{!hideTotalValue && <StyledTypographyNoWrap variant="h2">{formattedValue}</StyledTypographyNoWrap>}</StyledCard>;
      };
      $[64] = chart;
      $[65] = chartDrawing;
      $[66] = defaults;
      $[67] = drawingColorMaps;
      $[68] = elementsDescriptionsMap;
      $[69] = firstAxisDetails;
      $[70] = firstAxisDomain;
      $[71] = hideChart;
      $[72] = hideTotalValue;
      $[73] = newMetricsMap;
      $[74] = processedData;
      $[75] = respectColors;
      $[76] = secondAxisDomains;
      $[77] = t16;
    } else {
      t16 = $[77];
    }
    t8 = secondAxisDetails.map(t16);
    $[14] = chart;
    $[15] = chartDrawing;
    $[16] = chartSeriesColors;
    $[17] = chartType;
    $[18] = choicesMap;
    $[19] = defaults;
    $[20] = dimensionsMap;
    $[21] = drawingColorMaps;
    $[22] = elementsDescriptionsMap;
    $[23] = firstAxisDetails;
    $[24] = firstAxisDomain;
    $[25] = hideChart;
    $[26] = hideTotalValue;
    $[27] = id;
    $[28] = multipleElements;
    $[29] = newMetricsMap;
    $[30] = processedData;
    $[31] = respectColors;
    $[32] = secondAxis;
    $[33] = secondAxisDomains;
    $[34] = selections;
    $[35] = singleElements;
    $[36] = theme;
    $[37] = title;
    $[38] = transposedMetricsLists;
    $[39] = T0;
    $[40] = T1;
    $[41] = t10;
    $[42] = t5;
    $[43] = t6;
    $[44] = t7;
    $[45] = t8;
    $[46] = t9;
  } else {
    T0 = $[39];
    T1 = $[40];
    t10 = $[41];
    t5 = $[42];
    t6 = $[43];
    t7 = $[44];
    t8 = $[45];
    t9 = $[46];
  }
  let t11;
  if ($[78] !== T0 || $[79] !== t5 || $[80] !== t6 || $[81] !== t7 || $[82] !== t8) {
    t11 = <T0 containerName={t5} columnMinWidth={t6} fixedColumns={t7}>{t8}</T0>;
    $[78] = T0;
    $[79] = t5;
    $[80] = t6;
    $[81] = t7;
    $[82] = t8;
    $[83] = t11;
  } else {
    t11 = $[83];
  }
  let t12;
  if ($[84] !== T1 || $[85] !== t10 || $[86] !== t11 || $[87] !== t9) {
    t12 = <T1>{t9}{t10}{t11}</T1>;
    $[84] = T1;
    $[85] = t10;
    $[86] = t11;
    $[87] = t9;
    $[88] = t12;
  } else {
    t12 = $[88];
  }
  return t12;
};
export const ChartCardChartContainerSkeleton = t0 => {
  const $ = _c(9);
  const {
    cardSize,
    chartSize,
    view
  } = t0;
  const theme = useTheme();
  let t1;
  if ($[0] !== cardSize || $[1] !== chartSize || $[2] !== view) {
    t1 = getChartCardChartContainerSize({
      cardSize,
      chartSize,
      view
    });
    $[0] = cardSize;
    $[1] = chartSize;
    $[2] = view;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    containerWidth,
    containerHeight
  } = t1;
  const skeletonWidth = containerWidth;
  let t2;
  if ($[4] !== theme) {
    t2 = theme.spacing(8);
    $[4] = theme;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const skeletonHeight = `calc(${containerHeight}px + ${t2})`;
  let t3;
  if ($[6] !== skeletonHeight || $[7] !== skeletonWidth) {
    t3 = <Skeleton width={skeletonWidth} height={skeletonHeight} variant="rectangular" />;
    $[6] = skeletonHeight;
    $[7] = skeletonWidth;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
export default ChartCardPerformanceContainer;